import { PageState, Paginator } from 'primereact/paginator';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { navigateToSpecificNotification } from 'state/ducks/notifications/operations';
import { Routes } from 'state/ducks/routes';
import { setSelectedMachines } from 'state/ducks/table-settings/operations';
import { localized, localizedInterpolation } from 'state/i18n';
import { AppState } from 'state/store';
import { Spinner } from 'view/components/spinner/spinner';
import BiButton from 'view/shared/components/buttons/bi-button/bi-button';
import WhiteCard from 'view/shared/components/cards/white-card';
import { NoSubscriptionDialog } from 'view/shared/components/notification-utility/no-subscription-dialog';
import { cardDetailsStyles, cardGeneralClassNames } from '../../constants';
import { NotificationSubscriptionRows } from './notification-rows';
import './notification-subscription-list.scss';

const containerClassName = `${cardGeneralClassNames.classNameContainer} ${cardDetailsStyles.classNameContainer}`;

type Props = {
	machineDetailsId: number;
	showLimitedData?: boolean;
	hasSubscription?: boolean;
	serialNumber: string;
};

export const NotificationSubscriptionList: FC<Props> = React.memo((props: Props) => {
	const dispatch = useDispatch();
	const notificationDefinitions = useSelector((state: AppState) =>
		state.notificationsReducer.notificationDefinitions.filter(nd => nd.unitIds?.includes(props.machineDetailsId))
	);
	const loading = useSelector((state: AppState) => state.notificationsReducer.loading);
	const machinesSelected = useSelector((state: AppState) => state.machineSelectedReducer.machinesSelected);

	const [paginatorFirst, setPaginatorFirst] = useState(0);
	const [paginatorRows] = useState(11);
	const [totalRows, setTotalRows] = useState(0); //sum of users
	const [showDialog, setShowDialog] = React.useState<boolean>(false);

	useEffect(() => {
		let totalRows =
			notificationDefinitions?.map(def => def.notificationUsers.length).reduce((a, b) => a + b, 0) || 0;
		setTotalRows(totalRows);
	}, [notificationDefinitions]);

	const getList = () => {
		return notificationDefinitions
			?.flatMap(def =>
				def.notificationUsers.map(nu => (
					<NotificationSubscriptionRows
						key={nu.id}
						serialNumber={props.serialNumber}
						notificationUser={nu}
						notificationDefinition={def}
					/>
				))
			)
			.slice(paginatorFirst, paginatorFirst + paginatorRows);
	};

	const onPageChangeHandler = (e: PageState) => {
		setPaginatorFirst(e.first);
	};
	const navigationHelper = useCallback(() => {
		setSelectedMachines([props.machineDetailsId])(dispatch);
		const returnPath = `${Routes.MachineDetails}/${encodeURIComponent(props.serialNumber)}`;
		navigateToSpecificNotification(
			undefined,
			machinesSelected?.some(m => m.id === props.machineDetailsId),
			returnPath
		)(dispatch);
	}, [dispatch, machinesSelected, props.machineDetailsId, props.serialNumber]);

	const handleNotificationClicked = () => {
		if (!props.hasSubscription) {
			setShowDialog(true);
		} else {
			navigationHelper();
		}
	};
	const hideDialog = () => {
		setShowDialog(false);
	};
	const dialogOkPressed = () => {
		setShowDialog(false);
		navigationHelper();
	};

	const getContent = () => {
		if (!props.showLimitedData) {
			return (
				<div className="notification-subscription-list">
					<h5>{localized('NoSubscription')}</h5>
					<div className="disabled-text">
						{localizedInterpolation('EnableToSeeNotification', {
							area: totalRows,
						})}
					</div>
				</div>
			);
		}

		// If we already have data in redux then show that instead of a spinner
		if (totalRows !== 0) {
			return (
				<>
					<div className="notification-subscription-list">
						<div className={`margin-bottom-10px notification-subscription-grid`}>
							<b>{localized('NotificationName')}</b>
							<b>{localized('email')}</b>
							<b>{localized('GoToDefinition')}</b>
						</div>
						{getList()}
					</div>

					{totalRows > paginatorRows && (
						<div className="flex-end-row">
							<Paginator
								first={paginatorFirst}
								rows={paginatorRows}
								totalRecords={totalRows}
								onPageChange={onPageChangeHandler}
								template="PrevPageLink PageLinks NextPageLink"
							/>
						</div>
					)}
				</>
			);
		}

		// Render a spinner while we are waiting for a response from the API
		if (loading)
			return (
				<div className="margin-top-60px">
					<Spinner shouldOverlay={false} spinnerSize={'spinner-container-half-size'} />
				</div>
			);

		// Display message if there is no data
		if (totalRows === 0)
			return (
				<div>
					<p>{localized('NoNotificationsForCurrentDevice')}</p>
				</div>
			);
	};

	const topRightElement = (
		<>
			<BiButton colorTheme="org-dark-blue-with-grey-text" onClick={handleNotificationClicked}>
				{localized('AddNewNotification')}
			</BiButton>
			<NoSubscriptionDialog onOk={dialogOkPressed} onHide={hideDialog} title={localized('ThisMachineWithoutSubscription')} visible={showDialog}/>
		</>
	);

	return (
		<WhiteCard
			title={localized('Notifications')}
			classNameContainer={containerClassName}
			topRightElement={topRightElement}
		>
			{getContent()}
		</WhiteCard>
	);
});
